import { z } from "zod";
import { AiActionPolicyV1Schema } from "../ai-action-policy";
import { CalendarDateSchema } from "../campaign-creation";
import {
  SMART_CREATION_MAX_CAMPAIGNS,
  SmartCreationAdGroupStableKeySchema,
  SmartCreationDraftContentV2Schema,
  SmartCreationStrategyLaneSchema,
} from "../smart-creation";
import { SmartCreationReviewDiagnosticSchema } from "./diagnostics";
import {
  SmartCreationReviewGraphSchema,
  SmartCreationReviewPlanSettingsSchema,
  SmartCreationReviewProjectionsSchema,
} from "./graph";
import { SmartCreationReviewBudgetSuggestionSchema } from "./suggestions";
import { BidSchema, ContentHashSchema } from "./values";

export const SmartCreationReviewSummarySchema = z
  .object({
    campaignCount: z.number().int().positive().max(SMART_CREATION_MAX_CAMPAIGNS),
    includedCampaignCount: z.number().int().nonnegative().max(SMART_CREATION_MAX_CAMPAIGNS),
    excludedCampaignCount: z.number().int().nonnegative().max(SMART_CREATION_MAX_CAMPAIGNS),
    adGroupCount: z.number().int().positive(),
    productAdCount: z.number().int().positive(),
    targetCount: z.number().int().positive(),
    includedTargetCount: z.number().int().nonnegative(),
    excludedTargetCount: z.number().int().nonnegative(),
    negativeKeywordCount: z.number().int().nonnegative(),
    negativeProductCount: z.number().int().nonnegative(),
    totalDailyBudget: z.number().finite().nonnegative(),
    currencyCode: z.string().regex(/^[A-Z]{3}$/),
    startDate: CalendarDateSchema,
    endDate: CalendarDateSchema.nullable(),
  })
  .strict();
export type SmartCreationReviewSummary = z.infer<typeof SmartCreationReviewSummarySchema>;

export const SmartCreationAutomaticBidInitializationSchema = z
  .object({
    adGroupKey: SmartCreationAdGroupStableKeySchema,
    contextFingerprint: ContentHashSchema,
    fetchedAt: z.string().datetime(),
    source: z.literal("amazon_sp_theme_based_new_ad_group"),
    status: z.enum(["recommended", "partial", "unavailable"]),
    reason: z.string().nullable(),
    items: z
      .array(
        z
          .object({
            expressionType: z.enum(["close_match", "loose_match", "substitutes", "complements"]),
            initialBid: BidSchema,
            // Amazon reference values can include zero and are not executable bid bounds.
            bidValues: z.tuple([
              z.number().finite().nonnegative(),
              z.number().finite().nonnegative(),
              z.number().finite().nonnegative(),
            ]),
          })
          .strict(),
      )
      .max(4),
  })
  .strict();
export type SmartCreationAutomaticBidInitialization = z.infer<
  typeof SmartCreationAutomaticBidInitializationSchema
>;

export const SmartCreationReviewBidBoundsSuggestionSchema = z
  .object({
    source: z.enum(["amazon", "history", "amazon_and_history"]),
    min: BidSchema,
    max: BidSchema,
    targetAcos: z.number().finite().positive(),
    generatedAt: z.string().datetime(),
  })
  .strict()
  .refine((value) => value.min <= value.max, { message: "Invalid bid bounds" });
export type SmartCreationReviewBidBoundsSuggestion = z.infer<
  typeof SmartCreationReviewBidBoundsSuggestionSchema
>;

export const SmartCreationReviewWorkspaceSchema = z
  .object({
    schemaVersion: z.literal(2),
    workspaceRevision: z.number().int().positive(),
    basedOn: z
      .object({
        planRevision: z.number().int().positive(),
        previewGeneratorVersion: z.number().int().positive(),
        identityVersion: z.number().int().positive(),
        capabilityFingerprint: ContentHashSchema,
      })
      .strict(),
    status: z.enum(["ready", "preview_refresh_required", "confirmed"]),
    suggestedDraft: SmartCreationDraftContentV2Schema,
    draftContent: SmartCreationDraftContentV2Schema,
    budgetSuggestion: SmartCreationReviewBudgetSuggestionSchema.optional(),
    bidBoundsSuggestion: SmartCreationReviewBidBoundsSuggestionSchema.optional(),
    automaticBidInitializations: z.array(SmartCreationAutomaticBidInitializationSchema).optional(),
    pendingStrategyLanes: z.array(SmartCreationStrategyLaneSchema).optional(),
    strategyIntentCoverage: z
      .array(
        z
          .object({
            lane: SmartCreationStrategyLaneSchema,
            productAsins: z.array(z.string()).min(1),
          })
          .strict(),
      )
      .optional(),
    strategyPreparation: z
      .array(
        z
          .object({
            lane: SmartCreationStrategyLaneSchema,
            productAsins: z.array(z.string()).min(1),
            status: z.enum(["ready", "empty", "failed"]),
            reason: z.string().nullable(),
            retryable: z.boolean(),
          })
          .strict(),
      )
      .optional(),
    aiActionPolicy: AiActionPolicyV1Schema.optional(),
    planSettings: SmartCreationReviewPlanSettingsSchema,
    graph: SmartCreationReviewGraphSchema,
    projections: SmartCreationReviewProjectionsSchema,
    diagnostics: z.array(SmartCreationReviewDiagnosticSchema).max(500),
    summary: SmartCreationReviewSummarySchema,
    safety: z
      .object({
        campaignState: z.literal("PAUSED"),
        aiManagement: z.literal("OFF"),
        amazonWrites: z.literal(false),
      })
      .strict(),
    contentHash: ContentHashSchema,
  })
  .strict()
  .superRefine((workspace, ctx) => {
    const { graph, suggestedDraft, draftContent, summary } = workspace;

    if (
      workspace.basedOn.previewGeneratorVersion !== suggestedDraft.previewGeneratorVersion ||
      workspace.basedOn.identityVersion !== suggestedDraft.identityVersion ||
      workspace.basedOn.capabilityFingerprint !== suggestedDraft.capabilityFingerprint
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Review base metadata must match the suggested draft",
        path: ["basedOn"],
      });
    }
    if (
      suggestedDraft.previewGeneratorVersion !== draftContent.previewGeneratorVersion ||
      suggestedDraft.identityVersion !== draftContent.identityVersion ||
      suggestedDraft.capabilityFingerprint !== draftContent.capabilityFingerprint
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Effective draft content must retain the preview generator, identity and capability metadata",
        path: ["draftContent"],
      });
    }

    const lineageKeys = [
      ...graph.campaigns.map((entity) => entity.ref.lineageKey),
      ...graph.adGroups.map((entity) => entity.ref.lineageKey),
      ...graph.targets.map((entity) => entity.ref.lineageKey),
      ...graph.negatives.map((entity) => entity.ref.lineageKey),
    ];
    if (new Set(lineageKeys).size !== lineageKeys.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Review entity lineage keys must be globally unique",
        path: ["graph"],
      });
    }

    const campaignLineageKeys = new Set(graph.campaigns.map((campaign) => campaign.ref.lineageKey));
    const adGroupLineageKeys = new Set(graph.adGroups.map((adGroup) => adGroup.ref.lineageKey));
    const targetLineageKeys = new Set(graph.targets.map((target) => target.ref.lineageKey));
    const negativeLineageKeys = new Set(graph.negatives.map((negative) => negative.ref.lineageKey));
    const adGroupsByLineage = new Map(
      graph.adGroups.map((adGroup) => [adGroup.ref.lineageKey, adGroup]),
    );
    const targetsByLineage = new Map(
      graph.targets.map((target) => [target.ref.lineageKey, target]),
    );
    const negativesByLineage = new Map(
      graph.negatives.map((negative) => [negative.ref.lineageKey, negative]),
    );
    const effectiveAdGroupsByKey = new Map(
      draftContent.campaigns.flatMap((campaign) =>
        campaign.adGroups.map((adGroup) => [adGroup.itemKey, adGroup] as const),
      ),
    );
    const adGroupMemberships = graph.campaigns.flatMap((campaign) =>
      campaign.adGroupLineageKeys.map((lineageKey) => ({
        parentLineageKey: campaign.ref.lineageKey,
        lineageKey,
      })),
    );
    for (const [index, membership] of adGroupMemberships.entries()) {
      const adGroup = adGroupsByLineage.get(membership.lineageKey);
      if (!adGroup || adGroup.campaignLineageKey !== membership.parentLineageKey) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Campaign and Ad Group graph references must be reciprocal",
          path: ["graph", "campaigns", index, "adGroupLineageKeys"],
        });
      }
    }
    if (
      adGroupMemberships.length !== adGroupLineageKeys.size ||
      new Set(adGroupMemberships.map((membership) => membership.lineageKey)).size !==
        adGroupMemberships.length
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Every Ad Group must belong to exactly one Campaign",
        path: ["graph", "campaigns"],
      });
    }

    const targetMemberships: string[] = [];
    const negativeMemberships: string[] = [];
    for (const [index, adGroup] of graph.adGroups.entries()) {
      if (!campaignLineageKeys.has(adGroup.campaignLineageKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Ad Group must reference a Campaign in the canonical graph",
          path: ["graph", "adGroups", index, "campaignLineageKey"],
        });
      }
      for (const key of adGroup.targetLineageKeys) {
        targetMemberships.push(key);
        const target = targetsByLineage.get(key);
        if (!target || target.adGroupLineageKey !== adGroup.ref.lineageKey) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Ad Group and Target graph references must be reciprocal",
            path: ["graph", "adGroups", index, "targetLineageKeys"],
          });
        }
      }
      for (const key of adGroup.negativeLineageKeys) {
        negativeMemberships.push(key);
        const negative = negativesByLineage.get(key);
        if (!negative || negative.adGroupLineageKey !== adGroup.ref.lineageKey) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Ad Group and Negative graph references must be reciprocal",
            path: ["graph", "adGroups", index, "negativeLineageKeys"],
          });
        }
      }
    }
    for (const [label, memberships, expectedKeys] of [
      ["Target", targetMemberships, targetLineageKeys],
      ["Negative", negativeMemberships, negativeLineageKeys],
    ] as const) {
      if (
        memberships.length !== expectedKeys.size ||
        new Set(memberships).size !== memberships.length
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Every ${label} must belong to exactly one Ad Group`,
          path: ["graph", "adGroups"],
        });
      }
    }
    for (const [index, target] of graph.targets.entries()) {
      if (!adGroupLineageKeys.has(target.adGroupLineageKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Target must reference an Ad Group in the canonical graph",
          path: ["graph", "targets", index, "adGroupLineageKey"],
        });
      }
      const reviewAdGroup = adGroupsByLineage.get(target.adGroupLineageKey);
      const effectiveAdGroup = reviewAdGroup
        ? effectiveAdGroupsByKey.get(reviewAdGroup.ref.itemKey)
        : undefined;
      const projectedInLane = effectiveAdGroup?.targeting.targets.some(
        (candidate) => candidate.itemKey === target.ref.itemKey,
      );
      const typeMatchesLane =
        (target.type === "automatic" && effectiveAdGroup?.targeting.type === "automatic") ||
        (target.type === "keyword" &&
          effectiveAdGroup?.targeting.type === "keyword" &&
          target.matchType.effective === effectiveAdGroup.targeting.matchType) ||
        (target.type === "product" && effectiveAdGroup?.targeting.type === "product") ||
        (target.type === "category" && effectiveAdGroup?.targeting.type === "category") ||
        (target.type === "category_brand" && effectiveAdGroup?.targeting.type === "category_brand");
      if (!projectedInLane || !typeMatchesLane) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Target type and Match Type must agree with its Ad Group lane",
          path: ["graph", "targets", index, "adGroupLineageKey"],
        });
      }
    }
    for (const [index, negative] of graph.negatives.entries()) {
      if (!adGroupLineageKeys.has(negative.adGroupLineageKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Negative must reference an Ad Group in the canonical graph",
          path: ["graph", "negatives", index, "adGroupLineageKey"],
        });
      }
    }

    for (const [projectionName, projection] of Object.entries(workspace.projections)) {
      const keys = new Set(projection.campaignLineageKeys);
      if (
        keys.size !== campaignLineageKeys.size ||
        [...campaignLineageKeys].some((key) => !keys.has(key))
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${projectionName} projection must contain every canonical Campaign exactly once`,
          path: ["projections", projectionName, "campaignLineageKeys"],
        });
      }
    }

    const generatedCampaignKeys = draftContent.campaigns.map((campaign) => campaign.itemKey);
    const reviewCampaignKeys = graph.campaigns.map((campaign) => campaign.ref.itemKey);
    const generatedAdGroupKeys = draftContent.campaigns.flatMap((campaign) =>
      campaign.adGroups.map((adGroup) => adGroup.itemKey),
    );
    const reviewAdGroupKeys = graph.adGroups.map((adGroup) => adGroup.ref.itemKey);
    for (const [label, itemKeys, reviewKeys] of [
      ["Campaign", generatedCampaignKeys, reviewCampaignKeys],
      ["Ad Group", generatedAdGroupKeys, reviewAdGroupKeys],
    ] as const) {
      const generated = [...itemKeys].sort();
      const review = [...reviewKeys].sort();
      if (
        generated.length !== review.length ||
        generated.some((key, index) => key !== review[index])
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${label} graph must project the current draft exactly`,
          path: ["graph"],
        });
      }
    }
    const effectiveTargetKeys = draftContent.campaigns.flatMap((campaign) =>
      campaign.adGroups.flatMap((adGroup) =>
        adGroup.targeting.targets.map((target) => target.itemKey),
      ),
    );
    const reviewTargetKeys = graph.targets.flatMap((target) =>
      target.ref.itemKey ? [target.ref.itemKey] : [],
    );
    const effectiveTargets = [...effectiveTargetKeys].sort();
    const reviewTargets = [...reviewTargetKeys].sort();
    if (
      effectiveTargets.length !== reviewTargets.length ||
      effectiveTargets.some((key, index) => key !== reviewTargets[index])
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Materialized Target graph must project the current draft exactly",
        path: ["graph", "targets"],
      });
    }
    if (
      (workspace.status === "ready" || workspace.status === "confirmed") &&
      graph.targets.some((target) => target.ref.itemKey === null)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "A ready or confirmed Workspace cannot contain an unmaterialized Target",
        path: ["graph", "targets"],
      });
    }

    const excludedCampaignCount = graph.campaigns.filter(
      (campaign) => campaign.excluded.effective,
    ).length;
    const excludedTargetCount = graph.targets.filter((target) => target.excluded.effective).length;
    const includedCampaignLineageKeys = new Set(
      graph.campaigns
        .filter((campaign) => !campaign.excluded.effective)
        .map((campaign) => campaign.ref.lineageKey),
    );
    if (includedCampaignLineageKeys.size === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "At least one Campaign must remain included",
        path: ["graph", "campaigns"],
      });
    }
    for (const [index, adGroup] of graph.adGroups.entries()) {
      if (!includedCampaignLineageKeys.has(adGroup.campaignLineageKey)) continue;
      const hasIncludedTarget = adGroup.targetLineageKeys.some((lineageKey) => {
        const target = targetsByLineage.get(lineageKey);
        return target !== undefined && !target.excluded.effective;
      });
      if (!hasIncludedTarget) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Every included Ad Group must retain at least one included Target",
          path: ["graph", "adGroups", index, "targetLineageKeys"],
        });
      }
    }
    const productAdCount = graph.adGroups.reduce(
      (count, adGroup) => count + adGroup.products.length,
      0,
    );
    const expectedSummary = {
      campaignCount: graph.campaigns.length,
      includedCampaignCount: graph.campaigns.length - excludedCampaignCount,
      excludedCampaignCount,
      adGroupCount: graph.adGroups.length,
      productAdCount,
      targetCount: graph.targets.length,
      includedTargetCount: graph.targets.length - excludedTargetCount,
      excludedTargetCount,
      negativeKeywordCount: graph.negatives.filter((negative) => negative.type === "keyword")
        .length,
      negativeProductCount: graph.negatives.filter((negative) => negative.type === "product")
        .length,
    };
    for (const [field, expected] of Object.entries(expectedSummary)) {
      if (summary[field as keyof typeof expectedSummary] !== expected) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${field} must match the canonical Review graph`,
          path: ["summary", field],
        });
      }
    }
    const includedDailyBudget = graph.campaigns.reduce(
      (total, campaign) =>
        campaign.excluded.effective ? total : total + campaign.dailyBudget.effective,
      0,
    );
    if (Math.abs(summary.totalDailyBudget - includedDailyBudget) > 1e-8) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "totalDailyBudget must equal the included Campaign budget total",
        path: ["summary", "totalDailyBudget"],
      });
    }
    if (workspace.status === "ready" || workspace.status === "confirmed") {
      if (workspace.diagnostics.some((diagnostic) => diagnostic.blocking)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "A ready or confirmed Workspace cannot have blocking diagnostics",
          path: ["diagnostics"],
        });
      }
    }
  });
export type SmartCreationReviewWorkspace = z.infer<typeof SmartCreationReviewWorkspaceSchema>;

export const SmartCreationReviewWorkspaceResponseSchema = z
  .object({
    success: z.literal(true),
    data: SmartCreationReviewWorkspaceSchema,
  })
  .strict();
export type SmartCreationReviewWorkspaceResponse = z.infer<
  typeof SmartCreationReviewWorkspaceResponseSchema
>;
